import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRightIcon,
  TrophyIcon,
  MapPinIcon,
  UserGroupIcon,
  CalendarIcon,
  SparklesIcon,
  CheckCircleIcon,
  HomeIcon
} from '@heroicons/react/24/outline';
import { clubInfo, trainingSports } from '../data/content';
import SEO from '../components/SEO';

const Home: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-background-dark">
      <SEO 
        title="Startseite"
        description="HSV Berlin-Adlershof e.V. - Ihr Hundesportverein in Berlin seit 1948. Training für Unterordnung, Rally Obedience, Turnierhundesport und Agility. Gästetraining samstags – aktuell nur Warteliste."
        url="/"
      />
      {/* CTA Section - Werde Teil unserer starken Gemeinschaft */}
      <section className="relative h-[400px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/home/hero-main.jpg"
            alt="HSV Berlin-Adlershof Hundesportverein - Mitglieder und Hunde beim Training auf dem Vereinsgelände in Berlin-Adlershof"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="relative h-full flex items-center">
          <div className="container mx-auto px-4 max-w-5xl">
            {/* Hero-Content OHNE Animation - sofort sichtbar */}
            <div className="max-w-2xl">
              <h1 className="text-2xl md:text-5xl font-black text-white mb-4 md:mb-6 uppercase">
                Werde Teil unserer starken Gemeinschaft!
              </h1>
              <p className="text-base md:text-xl text-white/90 mb-6 md:mb-8">
                Entdecke die Welt des Hundesports gemeinsam mit uns.
                Ob Anfänger oder Fortgeschrittener – bei uns findet jeder seinen Platz.
              </p>
              <Link
                to="/kontakt"
                className="inline-flex items-center bg-primary-600 text-white px-6 py-3 md:px-8 md:py-4 rounded-lg font-bold hover:bg-primary-700 transition-colors text-base md:text-lg"
              >
                Jetzt Mitglied werden
                <ArrowRightIcon className="w-6 h-6 ml-3" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="section bg-neutral-50 dark:bg-background-dark-secondary">
        <div className="container mx-auto px-4 max-w-5xl">
          {/* Intro-Text OHNE Animation - sofort sichtbar */}
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-4xl font-bold mb-4 md:mb-6 text-neutral-900 dark:text-neutral-100">
              Herzlich Willkommen beim HSV Berlin-Adlershof e.V.
            </h2>
            <p className="text-base md:text-lg text-neutral-600 dark:text-neutral-400 max-w-4xl mx-auto">
              Seit über 75 Jahren fördern wir die harmonische Beziehung zwischen Mensch und Hund durch vielseitige
              sportliche Aktivitäten. Unser Verein bietet Ihnen und Ihrem vierbeinigen Begleiter die perfekte
              Umgebung, um gemeinsam zu wachsen, zu lernen und Spaß zu haben.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 md:gap-8">
            <div className="card p-4 md:p-6 text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                <TrophyIcon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-neutral-900 dark:text-neutral-100">
                Tradition & Erfahrung
              </h3>
              <p className="text-neutral-600 dark:text-neutral-400">
                Gegründet 1948, sind wir einer der ältesten Hundesportvereine Berlins mit bewährten Trainingsmethoden.
              </p>
            </div>

            <div className="card p-6 text-center">
              <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPinIcon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-neutral-900 dark:text-neutral-100">
                Ideale Lage
              </h3>
              <p className="text-neutral-600 dark:text-neutral-400">
                Unser großzügiges Trainingsgelände im Birkenwäldchen bietet optimale Bedingungen für alle Sportarten.
              </p>
            </div>

            <div className="card p-6 text-center">
              <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserGroupIcon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-neutral-900 dark:text-neutral-100">
                Familiäre Atmosphäre
              </h3>
              <p className="text-neutral-600 dark:text-neutral-400">
                Bei uns steht der respektvolle Umgang miteinander und die individuelle Förderung im Vordergrund.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sports Overview */}
      <section className="section">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-900 dark:text-neutral-100">
              Unsere Sportarten
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-400">
              Entdecken Sie unser vielfältiges Trainingsangebot
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {trainingSports.map((sport) => (
              <div
                key={sport.id}
                className="card overflow-hidden group cursor-pointer"
              >
                <Link to={sport.path}>
                  <div className="h-48 overflow-hidden bg-neutral-100 dark:bg-neutral-800">
                    <img
                      src={sport.image}
                      alt={sport.title}
                      className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold mb-2 text-neutral-900 dark:text-neutral-100">
                      {sport.title}
                    </h3>
                    <p className="text-sm text-primary-600 dark:text-primary-400 mb-3">
                      {sport.subtitle}
                    </p>
                    <p className="text-neutral-600 dark:text-neutral-400 text-sm mb-4">
                      {sport.description}
                    </p>
                    <span className="inline-flex items-center text-primary-600 dark:text-primary-400 font-medium text-sm group-hover:translate-x-1 transition-transform">
                      Mehr erfahren
                      <ArrowRightIcon className="w-4 h-4 ml-1" />
                    </span>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section bg-neutral-50 dark:bg-background-dark-secondary">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-900 dark:text-neutral-100">
              Warum Hundesport?
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-400">
              Vorteile für Sie und Ihren vierbeinigen Freund
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 md:gap-8 max-w-4xl mx-auto">
            <div className="card p-5 md:p-8">
              <h3 className="text-xl md:text-2xl font-semibold mb-4 md:mb-6 text-neutral-900 dark:text-neutral-100 flex items-center">
                <SparklesIcon className="w-6 h-6 mr-2 text-primary-600 dark:text-primary-400" />
                Für Ihren Hund
              </h3>
              <ul className="space-y-3">
                {['Artgerechte Auslastung', 'Soziale Kontakte', 'Geistige Förderung', 'Verbessertes Gehorsam'].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircleIcon className="w-5 h-5 text-primary-600 dark:text-primary-400 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-neutral-700 dark:text-neutral-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="card p-5 md:p-8">
              <h3 className="text-xl md:text-2xl font-semibold mb-4 md:mb-6 text-neutral-900 dark:text-neutral-100 flex items-center">
                <UserGroupIcon className="w-6 h-6 mr-2 text-primary-600 dark:text-primary-400" />
                Für Sie
              </h3>
              <ul className="space-y-3">
                {['Aktive Freizeitgestaltung', 'Neue Freundschaften', 'Besseres Verständnis für Ihren Hund', 'Gemeinsame Erfolgserlebnisse'].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircleIcon className="w-5 h-5 text-primary-600 dark:text-primary-400 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-neutral-700 dark:text-neutral-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Training Ground Gallery Section */}
      <section className="py-10 md:py-20 bg-primary-50 dark:bg-primary-900/10">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-5xl font-black text-neutral-900 dark:text-neutral-100 mb-3 md:mb-4 uppercase">
              Unser Trainingsgelände
            </h2>
            <p className="text-base md:text-lg text-neutral-600 dark:text-neutral-400 max-w-3xl mx-auto">
              Unsere modernisierten Trainingsflächen im Grünen bieten optimale Bedingungen für Hund und Halter,
              egal ob für den Breitensport oder intensive Turniervorbereitung.
            </p>
          </div>

          {/* Image Gallery Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-4 mb-8 md:mb-16">
            <div className="relative h-48 md:h-64 overflow-hidden rounded-lg shadow-lg">
              <img src="/images/facilities/training-field-1.jpg" alt="HSV Adlershof Trainingsplatz 1 - Große Grünfläche für Hundetraining im Birkenwäldchen Berlin" className="w-full h-full object-cover" />
            </div>
            <div className="relative h-48 md:h-64 overflow-hidden rounded-lg shadow-lg">
              <img src="/images/facilities/training-field-2.jpg" alt="HSV Adlershof Trainingsplatz 2 - Zweiter Übungsplatz für Agility und Unterordnung Berlin-Adlershof" className="w-full h-full object-cover" />
            </div>
            <div className="relative h-48 md:h-64 overflow-hidden rounded-lg shadow-lg">
              <img src="/images/facilities/aerial-view-1.jpg" alt="Luftaufnahme HSV Trainingsgelände - Hundesportverein Berlin-Adlershof am Birkenwäldchen" className="w-full h-full object-cover" />
            </div>
            <div className="relative h-48 md:h-64 overflow-hidden rounded-lg shadow-lg">
              <img src="/images/facilities/aerial-view-2.jpg" alt="Vogelperspektive Trainingsplatz - Gesamtansicht beider Trainingsplätze HSV Adlershof" className="w-full h-full object-cover" />
            </div>
            <div className="relative h-48 md:h-64 overflow-hidden rounded-lg shadow-lg">
              <img src="/images/facilities/aerial-view-3.jpg" alt="Vereinsgelände von oben - HSV Berlin-Adlershof e.V. mit Vereinsheim und Trainingsflächen" className="w-full h-full object-cover" />
            </div>
            <div className="relative h-48 md:h-64 overflow-hidden rounded-lg shadow-lg">
              <img src="/images/facilities/aerial-view-4.jpg" alt="Gesamtansicht Hundesportverein - Trainingsgelände im Grünen Berlin Treptow-Köpenick" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-primary-600 text-white">
        <div className="container mx-auto px-4 max-w-5xl text-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Bereit für den ersten Schritt?
            </h2>
            <p className="text-base md:text-xl mb-8 md:mb-12 max-w-3xl mx-auto opacity-95">
              Unsere Samstagsgruppe ist aktuell ausgebucht – trag dich gerne über das Kontaktformular auf die Warteliste ein.
              Wir melden uns, sobald ein Platz frei wird.
            </p>

            <div className="grid grid-cols-3 gap-3 md:gap-6 mb-8 md:mb-12 max-w-3xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 md:p-6">
                <CalendarIcon className="w-6 h-6 md:w-8 md:h-8 mx-auto mb-2 md:mb-3" />
                <h3 className="font-semibold mb-1 md:mb-2 text-sm md:text-base">Gästetraining</h3>
                <p className="opacity-90 text-xs md:text-base">Jeden Samstag</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 md:p-6">
                <SparklesIcon className="w-6 h-6 md:w-8 md:h-8 mx-auto mb-2 md:mb-3" />
                <h3 className="font-semibold mb-1 md:mb-2 text-sm md:text-base">Status</h3>
                <p className="opacity-90 text-xs md:text-base">Aktuell nur Warteliste</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 md:p-6">
                <MapPinIcon className="w-6 h-6 md:w-8 md:h-8 mx-auto mb-2 md:mb-3" />
                <h3 className="font-semibold mb-1 md:mb-2 text-sm md:text-base">Standort</h3>
                <p className="opacity-90 text-xs md:text-base">Am Birkenwäldchen</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/kontakt" className="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-neutral-100 transition-colors inline-flex items-center justify-center">
                Auf Warteliste setzen
                <ArrowRightIcon className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/trainings" className="bg-transparent text-white border-2 border-white px-8 py-4 rounded-lg font-semibold hover:bg-white/10 transition-colors">
                Unser Trainingsangebot
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;